import va from '@vercel/analytics';
import { ethers } from 'ethers';
import Head from 'next/head';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import wordmark from '../public/images/wordmark.png';
import styles from '../styles/ArtistInvite.module.css';
import uploadStyles from '../styles/Upload.module.css';
import { buildURLParams, validateEmail } from '../util';
import Message from './Message';

const mainnetProvider = new ethers.providers.InfuraProvider('homestead', 'a71874bbcb6a450398f24a7bbd436eda');

const ArtistInvite = ({
    title,
    description,
    metaImage,
    artistName,
    artistImage,
    messageBubbles,
    mintPath,
    personal,
    collectInstagram,
}: {
    title: string,
    description: string,
    metaImage: string,
    artistName: string,
    artistImage: any,
    messageBubbles: (string | JSX.Element)[],
    mintPath: string,
    personal: boolean,
    collectInstagram?: boolean,
}) => {
    const router = useRouter();

    const [email, setEmail] = useState<string>('');
    const [instagram, setInstagram] = useState<string>('');
    const [location, setLocation] = useState<string>('');
    const [ensOrAddress, setEnsOrAddress] = useState<string>('');
    const [resolvedAddress, setResolvedAddress] = useState<string>();
    const [useWallet, setUseWallet] = useState(false);

    useEffect(() => {
        if (!ensOrAddress) {
            setResolvedAddress(undefined);
            return;
        }
        if (ethers.utils.isAddress(ensOrAddress)) {
            setResolvedAddress(ensOrAddress.toLowerCase());
            return;
        }
        const delayDebounceFn = setTimeout(async () => {
            try {
                const resolved = await mainnetProvider.resolveName(ensOrAddress);
                if (resolved) {
                    setResolvedAddress(resolved.toLowerCase());
                } else {
                    setResolvedAddress(undefined);
                }
            } catch (e) {
                setResolvedAddress(undefined);
            }
        }, 200);
        return () => clearTimeout(delayDebounceFn);
    }, [ensOrAddress]);

    async function onClick() {
        if (personal) {
            va.track("press_cta_personal")
        } else {
            va.track("press_cta_link");
        }
        const params = { email, ethAddress: resolvedAddress, instagram, location, ref: router.query.ref as string | undefined };
        router.push(`${mintPath}?${buildURLParams(params)}`);
    }

    function getForm() {
        if (!collectInstagram) {
            if (!useWallet) {
                return <>
                    <div className={styles.input}>
                        <input type="email" name="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
                        <button className={styles.submit} onClick={onClick} disabled={!validateEmail(email)}>Submit</button>
                    </div>
                    <a className={styles.wallet} onClick={() => setUseWallet(true)}>I have an Ethereum wallet</a>
                </>;
            } else {
                return <>
                    <div className={styles.input}>
                        <input type="email" name="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
                    </div>
                    <div className={styles.input}>
                        <input placeholder="ENS or Ethereum address" value={ensOrAddress} onChange={(e) => setEnsOrAddress(e.currentTarget.value)} />
                    </div>
                    <button className={styles.submit} style={{ padding: '10px 20px', margin: '10px auto' }} onClick={onClick} disabled={!validateEmail(email) || !resolvedAddress}>Submit</button>
                    <a className={styles.wallet} onClick={() => setUseWallet(false)}>Go back to email only</a>
                </>
            }
        }

        if (!useWallet) {
            return <>
                <div className={styles.input}>
                    <input
                        type="text"
                        name="username"
                        placeholder="Instagram handle"
                        value={instagram}
                        autoCorrect="off" 
                        autoCapitalize="none"
                        onChange={(e) => setInstagram(e.currentTarget.value)}
                    />
                </div>
                <div className={styles.input}>
                    <input type="email" name="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
                </div>
                <div className={styles.input}>
                    <input type="text" name="location" placeholder="What city do you live in?" value={location} onChange={(e) => setLocation(e.currentTarget.value)} />
                </div>
                <button className={styles.submit} style={{ padding: '10px 20px', margin: '10px auto' }} onClick={onClick} disabled={!instagram || !validateEmail(email) || !location}>Submit</button>
                <a className={styles.wallet} onClick={() => setUseWallet(true)}>I have an Ethereum wallet</a>
            </>;
        } else {
            return <>
                <div className={styles.input}>
                    <input
                        type="text"
                        name="username"
                        placeholder="Instagram handle"
                        value={instagram}
                        autoCorrect="off" 
                        autoCapitalize="none"
                        onChange={(e) => setInstagram(e.currentTarget.value)}
                    />
                </div>
                <div className={styles.input}>
                    <input type="email" name="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
                </div>
                <div className={styles.input}>
                    <input type="text" name="location" placeholder="What city do you live in?" value={location} onChange={(e) => setLocation(e.currentTarget.value)} />
                </div>
                <div className={styles.input}>
                    <input placeholder="ENS or Ethereum address" value={ensOrAddress} onChange={(e) => setEnsOrAddress(e.currentTarget.value)} />
                </div>
                <button className={styles.submit} style={{ padding: '10px 20px', margin: '10px auto' }} onClick={onClick} disabled={!instagram || !validateEmail(email) || !location || !resolvedAddress}>Submit</button>
                <a className={styles.wallet} onClick={() => setUseWallet(false)}>Go back to email only</a>
            </>
        }
    }

    return <>
        <Head>
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="og:title" content={title} />
            <meta name="og:description" content={description} />
            <meta name="twitter:image" content={metaImage} />
            <meta name="og:image" content={metaImage} />
        </Head>
        <div className={styles.container}>
            <div className={uploadStyles.wordmarkContainer}>
                <Image src={wordmark} width={98.52} height={32} alt="Salsa wordmark" />
            </div>
            <Message
                authorImage={artistImage}
                authorName={artistName}
                content={messageBubbles}
            />

            {getForm()}
        </div>
    </>;
}


export default ArtistInvite;